import { audioFilename } from "./audio-filename.js";
import type { VoiceGender } from "./voices.js";

/**
 * Where the 例句 recordings are published.
 *
 * Under the site root like everything else the deploy uploads, so CloudFront
 * serves them from the same distribution, inside the same pricing plan, with
 * the same cache. `/audio/cidian/` is the 词典's, and the two sections share
 * the `audio/` prefix that the replication rule copies and that no release
 * prunes. See `docs/audio.md`.
 */
const audioRoot = "/audio/liju";

/** Which recording is wanted: whose sentence, and which voice. */
export interface LijuAudioAddress {
  /** The sentence's address, which is the directory its bundle is in. */
  readonly slug: string;

  /** Which of the two voices. */
  readonly voice: VoiceGender;
}

/**
 * Where one recording is served from.
 *
 * **The sentence itself is the directory**, unescaped, the way the page is
 * addressed. `/liju/一次就够了/` and
 * `/audio/liju/一次就够了/一次就够了_male.mp3` are the same arrangement, so a
 * person looking at either can guess the other.
 *
 * **No reading in the path, where the 词典 has one.** A 词典 recording is of a
 * reading, so 长 holds two different files and the reading has to be in the
 * address. A 例句 recording is of the sentence. The page carries 简体, two 繁體
 * writings, pinyin under two standards and bopomofo, and every one of them is
 * that same sentence said aloud, so there is one pair of files however many
 * ways the page writes the line.
 *
 * **The slug rather than the sentence, and they are nearly always the same.**
 * An address is the sentence with its punctuation dropped, and `LijuSlugs`
 * gives the second of a punctuation-only pair a `-2`. Keying the file on the
 * slug is what makes the address unique; keying it on the text would put
 * 我们开始吧。 and 我们开始吧！ on one file. What is said into the microphone is
 * still the sentence, punctuation and all.
 * @param address - The sentence and the voice.
 * @returns The site-relative path.
 */
export function lijuAudioPath(address: LijuAudioAddress): string {
  return `${audioRoot}/${address.slug}/${audioFilename(address.slug, address.voice)}`;
}

/**
 * Where a recording is kept, relative to the repository root.
 *
 * The same string the bucket keys it under, since the site is synced to the
 * root of that bucket. `audio/liju/一次就够了/一次就够了_male.mp3` is the path
 * and the key. A slash in front makes it the address.
 *
 * **Outside `articles/` and outside git**, the way the 词典's are and for the
 * reasons `docs/audio.md` records: 41,734 committed files took the packed
 * repository from 204 MiB to 617 MiB, and 41,536 directories under Hugo's
 * watch took a single `hugo --watch` to 172,350 open file descriptors. The
 * 例句 add about 27,900 files, and none of them is committed either.
 * @param address - The sentence and the voice.
 * @returns The path under the repository root, which is also the bucket key.
 */
export function lijuAudioFile(address: LijuAudioAddress): string {
  return lijuAudioPath(address).replace(/^\//u, "");
}
